<script lang="ts">
  let {
    value = $bindable(''),
    placeholder,
    disabled = false,
    invalid = false,
    rows = 3,
    label,
    helper,
    error,
    id = `textarea-${Math.random().toString(36).slice(2, 9)}`,
    oninput,
    class: className = ''
  }: {
    value?: string;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    rows?: number;
    label?: string;
    helper?: string;
    error?: string;
    id?: string;
    oninput?: (e: Event) => void;
    class?: string;
  } = $props();

  const helperId = $derived(helper ? `${id}-helper` : undefined);
  const errorId = $derived(error ? `${id}-error` : undefined);
  const describedBy = $derived(errorId ?? helperId ?? undefined);
</script>

<div class="field {className}" data-invalid={invalid || undefined} data-disabled={disabled || undefined}>
  {#if label}
    <label class="label" for={id}>{label}</label>
  {/if}
  <textarea
    {id}
    bind:value
    {placeholder}
    {disabled}
    {rows}
    aria-invalid={invalid}
    aria-describedby={describedBy}
    {oninput}
  ></textarea>
  {#if error}
    <span id={errorId} class="error">{error}</span>
  {:else if helper}
    <span id={helperId} class="helper">{helper}</span>
  {/if}
</div>

<style>
  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .label {
    font-size: 0.75rem;
    font-weight: var(--weight-semibold);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  textarea {
    width: 100%;
    min-height: 80px;
    padding: var(--space-3);
    font-family: var(--font-sans);
    font-size: var(--text-body);
    line-height: 1.6;
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    caret-color: var(--color-accent);
    resize: vertical;
    transition:
      border-color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }

  textarea::placeholder {
    color: var(--color-text-muted);
    opacity: 1;
  }

  textarea:hover:not(:disabled) {
    border-color: var(--color-text-faint);
  }

  textarea:focus-visible {
    border-color: var(--color-accent);
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: -2px;
  }

  textarea:disabled {
    background: var(--color-surface-sunken);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }

  .field[data-invalid] textarea {
    border-color: var(--color-danger);
  }

  .field[data-invalid] textarea:focus-visible {
    outline-color: var(--color-danger);
  }

  .helper {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .error {
    font-size: var(--text-sm);
    color: var(--color-danger);
  }
</style>